import type {
  AppErrorCode,
  CreatePostInput,
  DashboardStats,
  MediaAttachment,
  PostWithTargets,
  ProviderCapabilities,
  PublicAccount,
  QueueFilters,
  UpdatePostInput,
} from '@postpilot/core/domain';
import { clearToken, getToken } from './session';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

/** A signed-in user, as the auth endpoints return it. */
export interface AuthUser {
  id: string;
  email: string;
}

/**
 * The typed API client.
 *
 * Every method's input and output type comes from `@postpilot/core/domain` — the
 * same module the server validates against. So a change to the wire format is a
 * compile error on both sides in the same commit, rather than a runtime surprise
 * in whichever one you forgot to update.
 */

/** A failed request, carrying the server's error code so callers can branch on it. */
export class ApiError extends Error {
  constructor(
    readonly code: AppErrorCode | string,
    message: string,
    readonly status: number,
    /** Per-field messages, for wiring a failed validation back into the form. */
    readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True if retrying the same request could plausibly work. */
  get isRetryable(): boolean {
    return this.status >= 500 || this.code === 'rate_limited' || this.code === 'network_error';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();

  const headers = new Headers(init.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  // Don't set Content-Type on FormData — the browser must add the multipart
  // boundary itself, and setting it manually strips that and breaks the upload.
  if (init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers,
      credentials: 'include', // the OAuth flow rides on a cookie
    });
  } catch {
    // fetch only rejects on a *network* failure — the API being down, DNS, CORS.
    // Give that its own code so the UI can say "check your connection" instead
    // of "something went wrong".
    throw new ApiError('network_error', 'Could not reach the server. Check your connection.', 0);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const body = await response.json().catch(() => null);

  if (!response.ok) {
    // A rejected token is dead weight — drop it so the app falls back to the
    // login screen instead of retrying with a credential the server won't accept.
    // (Not on /api/auth/login, where a 401 just means "wrong password".)
    if (response.status === 401 && !path.startsWith('/api/auth/')) {
      clearToken();
    }
    const error = body?.error;
    throw new ApiError(
      error?.code ?? 'internal_error',
      error?.message ?? `Request failed (${response.status})`,
      response.status,
      error?.details?.fields,
    );
  }

  return body as T;
}

export const api = {
  // --- Auth ----------------------------------------------------------------
  auth: {
    register: (email: string, password: string) =>
      request<{ token: string; user: AuthUser }>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),

    login: (email: string, password: string) =>
      request<{ token: string; user: AuthUser }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),

    me: () => request<{ user: AuthUser }>('/api/auth/me').then((r) => r.user),
  },

  // --- Accounts ------------------------------------------------------------
  accounts: {
    list: () => request<{ accounts: PublicAccount[] }>('/api/accounts').then((r) => r.accounts),

    disconnect: (id: string) =>
      request<{ success: true }>(`/api/accounts/${id}`, { method: 'DELETE' }),

    limits: (id: string) =>
      request<{ remaining: number; allowed: boolean; retryAfterSeconds: number }>(
        `/api/accounts/${id}/limits`,
      ),
  },

  // --- OAuth ---------------------------------------------------------------
  oauth: {
    /**
     * Returns the URL to send the user to. We do NOT navigate here — the caller
     * does, so it can show a "redirecting…" state first rather than the page
     * appearing to freeze.
     */
    start: (returnTo = '/accounts') =>
      request<{ authorizationUrl: string }>('/api/oauth/x/start', {
        method: 'POST',
        body: JSON.stringify({ returnTo }),
      }).then((r) => r.authorizationUrl),

    provider: () =>
      request<{ active: string; displayName: string; capabilities: ProviderCapabilities }>(
        '/api/oauth/providers',
      ),
  },

  // --- Posts ---------------------------------------------------------------
  posts: {
    list: (filters: Partial<QueueFilters> = {}) => {
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filters)) {
        if (value === undefined || value === null || value === '') continue;
        // Arrays go out as repeated keys (?status=a&status=b), which is what the
        // API's `normalizeQuery` expects.
        if (Array.isArray(value)) {
          for (const item of value) params.append(key, String(item));
        } else {
          params.set(key, String(value));
        }
      }

      return request<{
        posts: PostWithTargets[];
        total: number;
        page: number;
        pageSize: number;
      }>(`/api/posts?${params}`);
    },

    get: (id: string) => request<{ post: PostWithTargets }>(`/api/posts/${id}`).then((r) => r.post),

    create: (input: CreatePostInput) =>
      request<{ post: PostWithTargets }>('/api/posts', {
        method: 'POST',
        body: JSON.stringify(input),
      }).then((r) => r.post),

    update: (id: string, input: UpdatePostInput) =>
      request<{ post: PostWithTargets }>(`/api/posts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }).then((r) => r.post),

    remove: (id: string) => request<{ success: true }>(`/api/posts/${id}`, { method: 'DELETE' }),

    /** Re-queues only the targets that failed. See the API route for why. */
    retry: (id: string, accountIds?: string[]) =>
      request<{ post: PostWithTargets }>(`/api/posts/${id}/retry`, {
        method: 'POST',
        body: JSON.stringify({ accountIds }),
      }).then((r) => r.post),
  },

  // --- Media ---------------------------------------------------------------
  media: {
    upload: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return request<{ media: MediaAttachment }>('/api/media', {
        method: 'POST',
        body: form,
      }).then((r) => r.media);
    },

    remove: (path: string) =>
      request<{ success: true }>('/api/media', {
        method: 'DELETE',
        body: JSON.stringify({ path }),
      }),
  },

  // --- Stats ---------------------------------------------------------------
  stats: () => request<{ stats: DashboardStats }>('/api/stats').then((r) => r.stats),
};
